import { BookingAvailabilityGen } from "@/services/booking/v1/data-contracts";
import { getAvailableDaysAsWeekDayString, stringifyWeekDays } from "./availabilityDisplayHelper";
import i18n from "@/plugins/I18nPlugin";

/**
 * Parses a given date to a simple string representation: dd.mm.yyyy.
 * @param date - to be parsed to a string
 * @returns date string dd.mm.yyy
 */
export function simpleDate(date: string, locale = "de-de"): string {
  return new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric"
  });
}

/**
 * Returns DD.MM
 * @param date the string to parse to date
 * @returns
 */
export function dayMonthDate(date: string): string {
  const d = new Date(date);
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}`;
}

/**
 * Returns a simple date where 1..9 becomes 01...09
 * @param date - to be parsed to a string
 * @returns date string dd.mm.yyy
 */
export function simpleDoubleDigitDate(date: string, locale = "de-de"): string {
  const simple = simpleDate(date, locale);

  return doubleDigit(simple).join(".");
}

function doubleDigit(simple: string) {
  const simplyDoubleDigited: string[] = [];
  simple.split(".").forEach((s) => {
    if (Number(s) < 10) {
      simplyDoubleDigited.push(`0${s}`);
    } else {
      simplyDoubleDigited.push(s);
    }
  });
  return simplyDoubleDigited;
}

/**
 * Parses a given date to a simple string representation: dd.M yyyy, hh:mm.
 * @param date - to be parsed to a string
 * @returns date string dd.M yyyy, hh:mm.
 */
export function detailedDate(date: string | number, locale = "de-de") {
  return new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  });
}

/**
 * Parses a given date to a simple string representation: dd.M yyyy, hh:mm.
 * @param date - to be parsed to a string
 * @returns date string dd.M yyyy, hh:mm.
 */
export function ddmmyyyhhmm(date: string | number, locale = "de-de") {
  return new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  });
}

/**
 * Parses a given date to a simple string representation: dd.M yyyy
 * @param date - to be parsed to a string
 * @returns date string d, dd.M yyyy.
 */
export function detailedDateWithDay(date: string, locale = "de-de") {
  return new Date(date).toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

/**
 * Formats a date as a string including weekday, day, month and year.
 * @param date - ISO date string
 * @returns date string d, dd.M yyyy.
 */
export function detailedDateWithDayTranslated(date: string) {
  const d = new Date(date);

  const weekday = i18n.t(`components.fleet.FleetHomeVehicleAppointmentTimeline.${d.getDay()}`);
  const day = d.getDate();
  const month = i18n.t(`time.months.${d.getMonth()}`);
  const year = d.getFullYear();

  return `${weekday}, ${day} ${month} ${year}`;
}

/**
 * Returns: "Montag, der 24.11.2025"
 * @param date ISO string or any date-parsable string
 * @param locale BCP-47 locale, default "de-DE"
 */
export function dottedDateWithDayAndDer(date: string | number, locale = "de-DE"): string {
  const d = new Date(date);

  const weekday = d.toLocaleDateString(locale, { weekday: "long" });
  const dotted = d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });

  const weekdayCap = weekday.charAt(0).toLocaleUpperCase() + weekday.slice(1);

  return `${weekdayCap}, der ${dotted}`;
}

/**
 * Parses a given date to the ISO date used in the date picker.
 * @param date - to be parsed to a string.
 * @returns date string om ISO format.
 */
export function toISOString(date: string) {
  return new Date(date).toISOString().substr(0, 10);
}

/**
 * Helper to transform the date when it is given as "yyyy-mm-dd" into "dd.mm.yyyy"
 * @param date date given as yyyy-mm-dd
 * @returns date in "dd.mm.yyyy"
 */
export function convertDashedToDotted(date: string) {
  return date.slice(8, 10) + "." + date.slice(5, 7) + "." + date.slice(0, 4);
}

/**
 * Calculate the difference between today and the given month and years in months
 *
 * @param month (1 is january)
 * @param year
 */
export function calculateMonthDifference(month: number, year: number) {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const yearDifference = year - currentYear;
  const monthDifference = month - currentMonth;

  return yearDifference * 12 + monthDifference - 1;
}

/**
 * Calculate the difference between today and the given month and years in days
 *
 * @param date
 */
export function calculateDayDifference(date: Date, currentDate = new Date()) {
  const secondDifference = date.getTime() - currentDate.getTime();
  const dayDiffernence = secondDifference / (1000 * 3600 * 24);

  return Math.floor(dayDiffernence + 1);
}

export function getDays(date: Date) {
  return date.getTime() / (1000 * 3600 * 24);
}

/**
 * Returns HH:MM
 * @param date the date containing the time
 */
export function formatHoursAndMinutes(date: Date | string) {
  const d = typeof date === "string" ? new Date(date) : date;
  return doubleDigit(`${d.getHours()}.${d.getMinutes()}`).join(":");
}

/**
 * Returns HH:MM:SS
 * @param date the date containing the time
 */
export function formatHoursAndMinutesSeconds(date: Date | string) {
  const d = typeof date === "string" ? new Date(date) : date;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(
    d.getSeconds()
  ).padStart(2, "0")}`;
}

/**
 * Returns HH:MM
 * @param date the date containing the time
 */
export function formatUTCHoursAndMinutes(date: Date) {
  return doubleDigit(`${date.getUTCHours()}.${date.getUTCMinutes()}`).join(":");
}

/**
 * Returns YYYY-MM-DD (local time)
 * @param date the date containing the date string
 * @returns YYYY-MM-DD
 */
export function formatYearsMonthDay(date: Date) {
  return `${date.getFullYear().toString().padStart(4, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
    .getDate()
    .toString()
    .padStart(2, "0")}`;
}

/**
 * Returns YYYY-MM-DD (UTC)
 * @param date the date containing the date string
 * @returns YYYY-MM-DD
 */
export function formatUTCYearsMonthDay(date: Date) {
  return `${date.getUTCFullYear().toString().padStart(4, "0")}-${(date.getUTCMonth() + 1).toString().padStart(2, "0")}-${date
    .getUTCDate()
    .toString()
    .padStart(2, "0")}`;
}

/**
 * Returns YYYY-MM
 * @param date the date containing the date string
 * @returns YYYY-MM
 */
export function formatYearsMonth(date: Date) {
  return `${date.getFullYear().toString().padStart(4, "0")}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
}

/**
 * Returns YYYY-MM-DD in Europe/Berlin timezone.
 * Mirrors backend getBerlinDateKey() in quota-allocation.logic.ts.
 * @param date the date to convert
 * @returns YYYY-MM-DD in Berlin timezone
 */
export function toBerlinDateKey(date: Date): string {
  const parts = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);

  const y = parts.find((p) => p.type === "year")!.value;
  const m = parts.find((p) => p.type === "month")!.value;
  const d = parts.find((p) => p.type === "day")!.value;
  return `${y}-${m}-${d}`;
}

export function displayMsAsMinutesAndHours(ms: number) {
  return displaySecondsAsMinutesAndHours(ms / 1000);
}

export function displaySecondsAsMinutesAndHours(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  const hoursToDisplay = h > 0 ? `${h}h` : "";
  const minutesToDisplay = m > 0 ? `${m}m` : "";

  return hoursToDisplay + minutesToDisplay;
}

/**
 * Returns HH:MM:SS
 * @param seconds time we want to display in ms
 * @returns
 */
export function msToTime(ms: number): string {
  const seconds = ms / 1000;
  const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, "0"),
    m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, "0"),
    s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");

  return h + ":" + m + ":" + s;
}

/**
 * Like nsToTime but without the seconds.
 * .slice(0,5) will return only hh:mm of the string.
 */
export function msToHoursAndMinutes(ms: number) {
  return msToTime(ms).slice(0, 5);
}

/**
 * Compact human-readable duration: "12s", "3m", "3m05s", "1h", "1h05m".
 * Drops zero-valued lower units. Designed for HUD-style stat displays where
 * tabular precision matters but full HH:MM:SS would be too noisy.
 */
export function msToShortDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return minutes === 0 ? `${hours}h` : `${hours}h${minutes}m`;
  }
  return seconds === 0 ? `${minutes}m` : `${minutes}m${seconds}s`;
}

/**
 * Returns ms as a number
 * @param timeString hour and minute time expressed as hh:mm
 */
export function timeToMs(timeString: string): number {
  const [hours, minutes] = timeString.split(":");
  return (+hours * 60 * 60 + +minutes * 60) * 1000;
}

export function msToDays(ms: number): number {
  return ms / (24 * 60 * 60 * 1000);
}

export function daysToMs(days: number): number {
  return days * 24 * 60 * 60 * 1000;
}

/**
 * Format a timestamp as YYYY-MM-DD HH:MM
 * @param msSinceEpochTime Unix epoch time. The timestamp is multiplied by 1000 internally.
 * @example 1682447335
 * @returns
 */
export function formatAsFullDateAndFullHour(msSinceEpochTime: number): string {
  const fullDate = new Date(msSinceEpochTime);
  const justDate = formatYearsMonthDay(fullDate);

  // Get just the hour and the minutes(calendar requirements)
  const justTime = fullDate.toLocaleTimeString("de-DE", { hour12: false }).slice(0, -3);

  return justDate + " " + justTime;
}

/**
 * Function to display the available days in the availability array as short week days separated by a character. Default separator is " | ".
 * Example: "mon | tue | wed"
 * Used for OnlineBooking
 */
export function displayAvailableWeekDaysAsShortString(
  availability: BookingAvailabilityGen[],
  separator = " | "
): string {
  const weekDays = getAvailableDaysAsWeekDayString(availability);
  // Get not "monday" but "Mo"
  const shortenWeekDays = weekDays.map((weekDay) => weekDay.charAt(0).toLocaleUpperCase() + weekDay[1]);

  return stringifyWeekDays(shortenWeekDays, separator);
}

/**
 *
 */
export class DateVuetifyHelper {
  /**
   * date that shoul be manupulated
   */
  originalDate!: Date;

  constructor(date: string) {
    this.originalDate = new Date(date);
  }

  /**
   * time in HH:MM
   */
  get time() {
    return formatHoursAndMinutes(this.originalDate);
  }

  /**
   * time in HH:MM
   */
  set time(time: string) {
    let newDate = new Date(this.originalDate);
    newDate = new Date(new Date(newDate).setHours(+time.substring(0, 2)));
    newDate = new Date(new Date(newDate).setMinutes(+time.substring(3, 5)));

    this.originalDate = new Date(newDate);
  }

  /**
   * date in format YYYY-MM-DD
   */
  get date(): string {
    return this.originalDate.toISOString().substring(0, 10);
  }

  /**
   * date in format YYYY-MM-DD
   */
  set date(date: string) {
    const copy = new Date(date);

    const newDate = new Date(date);
    newDate.setDate(copy.getDate());
    newDate.setMonth(copy.getMonth());
    newDate.setFullYear(copy.getFullYear());
    newDate.setMinutes(copy.getMinutes());
    newDate.setHours(copy.getHours());
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    this.originalDate = new Date(newDate);
  }

  /**
   * date in format YYYY-MM-DD
   */
  get localeDate(): string {
    return simpleDoubleDigitDate(this.originalDate.toISOString());
  }

  /**
   * isodatestring
   */
  get isoDate(): string {
    return this.originalDate.toISOString();
  }

  /**
   * isodatestring
   */
  set isoDate(isoDate: string) {
    this.originalDate = new Date(isoDate);
  }
}

export function getLastChanged(
  lastModified: string,
  translate: (v: string, p?: Record<string, string>) => string
): string {
  const millisecondDifference = new Date().getTime() - new Date(lastModified).getTime();
  const days = Math.floor(millisecondDifference / (1000 * 3600 * 24));

  if (days) {
    if (days === 1) {
      return translate("sign.DocumentDetail.yesterday");
    }

    return translate("sign.DocumentDetail.dayDifference", { days: days.toString() });
  }

  const hoursInDecimal = millisecondDifference / (1000 * 3600);
  const hours = Math.floor(hoursInDecimal);
  if (hours) {
    if (hours === 1) {
      return translate("sign.DocumentDetail.anHourAgo");
    }

    return translate("sign.DocumentDetail.hourDifference", { hours: hours.toString() });
  }

  const minutesInDecimal = hoursInDecimal - hours;
  const minutes = Math.floor(minutesInDecimal * 60);
  if (minutes) {
    return translate("sign.DocumentDetail.minuteDifference", { minutes: minutes.toString() });
  }

  return translate("sign.DocumentDetail.noDifference");
}

/**
 * Checks if string is iso date encoding
 * @param str
 * @returns
 */
export function isIsoDateString(str: string) {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
  const d = new Date(str);
  return d instanceof Date && !isNaN(d.getTime()) && d.toISOString() === str; // valid date
}

export type Quarter = 1 | 2 | 3 | 4;

/**
 * Returns the calendar quarter (1-4) of the given date, using UTC.
 */
export function getCurrentQuarter(date: Date = new Date()): Quarter {
  return (Math.floor(date.getUTCMonth() / 3) + 1) as Quarter;
}

/**
 * Returns the ISO-8601 timestamp of the first instant of the given quarter, UTC.
 * Example: getQuarterStartISO(2026, 2) === "2026-04-01T00:00:00.000Z"
 */
export function getQuarterStartISO(year: number, quarter: Quarter): string {
  return new Date(Date.UTC(year, (quarter - 1) * 3, 1, 0, 0, 0, 0)).toISOString();
}

/**
 * Returns the ISO-8601 timestamp of the last millisecond of the given quarter, UTC.
 * Example: getQuarterEndISO(2026, 2) === "2026-06-30T23:59:59.999Z"
 */
export function getQuarterEndISO(year: number, quarter: Quarter): string {
  return new Date(Date.UTC(year, quarter * 3, 0, 23, 59, 59, 999)).toISOString();
}
